import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './AffiliateAgreementModal.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNDASignApi, fetchgetcheckbalance, fetchkycstatus } from '../Redux/AdminSlices/withdrawrequestslice';
import { updateUserNdaUrl } from '../auth/authSlice';
import { toast } from 'react-toastify';

export default function AffiliateAgreementModal({ isOpen, onSuccess }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    confirmed: false
  });
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [signedPdfUrl, setSignedPdfUrl] = useState(null);

  const canvasRef = useRef(null);

  // Pre-fill user information if available
  useEffect(() => {
    if (user) {
      const u = user?.user || user;
      const fName = u?.first_name || u?.firstName || '';
      const lName = u?.last_name || u?.lastName || '';
      const addr = u?.address || u?.street_address || '';
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || fName,
        lastName: prev.lastName || lName,
        address: prev.address || addr
      }));
    }
  }, [user]);

  // Canvas drawing coordinate helper
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const coords = getCoordinates(e);
    if (!coords) return;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const coords = getCoordinates(e);
    if (!coords) return;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setSignature(canvas.toDataURL());
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setSignature(canvasRef.current.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
    }
  };

  // Setup canvas resolution
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (isOpen && step === 2) {
      const timer = setTimeout(setupCanvas, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, step, setupCanvas]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    if (!formData.firstName || !formData.lastName || !formData.address || !formData.confirmed) {
      toast.error('Please complete all required fields and confirm the agreement');
      return;
    }

    if (!signature) {
      toast.error('Please provide your signature');
      return;
    }

    const currentDate = new Date().toISOString().split('T')[0];
    const payload = {
      send_email: true,
      date1: currentDate,
      name1: fullName,
      address1: formData.address,
      date2: currentDate,
      name2: fullName,
      title: '',
      signature2: signature,
      date3: currentDate,
      address2: formData.address
    };

    setSubmitting(true);
    try {
      const result = await dispatch(fetchNDASignApi(payload)).unwrap();
      const pdfUrl =
        result?.data?.pdf_url ||
        result?.data?.signed_pdf_url ||
        result?.pdf_url ||
        result?.signed_pdf_url ||
        'signed';

      setSignedPdfUrl(pdfUrl);
      dispatch(updateUserNdaUrl(pdfUrl));

      // Update localStorage authState so nda_url and kyc_status are updated
      try {
        const savedAuth = localStorage.getItem('authState');
        if (savedAuth) {
          const parsed = JSON.parse(savedAuth);
          if (parsed?.user) {
            parsed.user.nda_url = pdfUrl;
            parsed.user.kyc_status = 'verified';
            if (parsed.user.user) {
              parsed.user.user.nda_url = pdfUrl;
              parsed.user.user.kyc_status = 'verified';
            }
            localStorage.setItem('authState', JSON.stringify(parsed));
          }
        }
      } catch {
        // ignore storage parse error
      }

      const uid = user?.user?.id || user?.id;
      if (uid) {
        dispatch(fetchgetcheckbalance(uid));
        dispatch(fetchkycstatus(uid));
      }

      toast.success('Recruiter Agreement signed successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      const msg = err?.message || err?.data?.message || 'Failed to sign agreement. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    setShowSuccessModal(false);
    if (onSuccess) {
      onSuccess(signedPdfUrl);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="aam-overlay">
        <div className="aam-wrapper" onClick={(e) => e.stopPropagation()}>
          <div className="aam-header">
            <h3 className="aam-title">Recruiter Agreement &amp; Digital Signature</h3>
          </div>

          <div className="aam-body">
            {/* Step 1: Document Review & Confirmation */}
            <div className="aam-document-section">
              <div className="aam-document-header">
                <div className="aam-document-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>

                <div className="aam-document-title-section">
                  <h4 className="aam-document-title">Recruiter Agreement Required to Access Platform</h4>
                  <p className="aam-document-subtitle">
                    Before you can access your affiliate dashboard and start using the platform, you are required to review and digitally sign the Recruiter Agreement.
                    This agreement is mandatory for compliance purposes.
                  </p>
                </div>

                <button
                  type="button"
                  className="aam-btn-preview"
                  onClick={() => setShowPreview(true)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Preview Full Document
                </button>
              </div>

              <div className="aam-content-preview">
                <div className="aam-content-item">
                  <div className="aam-content-badge">1</div>
                  <div className="aam-content-text">
                    <strong>By signing below, you confirm that:</strong>
                    <ul style={{ marginTop: '4px', paddingLeft: '18px', listStyle: 'disc' }}>
                      <li>You have read and understood the Recruiter Agreement.</li>
                      <li>You agree to all terms and conditions outlined in the document.</li>
                      <li>Your digital signature is legally binding and equivalent to a handwritten signature.</li>
                    </ul>
                  </div>
                </div>

                <div className="aam-content-item">
                  <div className="aam-content-badge">2</div>
                  <div className="aam-content-text">
                    <strong>Reference Document:</strong> The complete Recruiter Agreement is available for your review.
                    <span style={{ display: 'block', marginTop: '2px', color: '#5B6B63', fontSize: 12 }}>
                      Recruiter Agreement v. 1.15.26
                    </span>
                  </div>
                </div>

                <div className="aam-content-item">
                  <div className="aam-content-badge">3</div>
                  <div className="aam-content-text">
                    <strong>Once signed:</strong> This agreement will be securely stored and your access to all portal features will be activated immediately.
                  </div>
                </div>
              </div>
            </div>

            {/* Step 1 Actions */}
            {step === 1 && (
              <>
                <div className="aam-form-group">
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      lineHeight: '1.5'
                    }}
                  >
                    <input
                      type="checkbox"
                      name="confirmed"
                      checked={formData.confirmed}
                      onChange={handleInputChange}
                      style={{ marginTop: '3px', cursor: 'pointer' }}
                    />
                    <span>
                      I confirm that I have read, understood, and agree to the terms of the Recruiter Agreement and consent to sign this agreement electronically.
                    </span>
                  </label>
                </div>

                <div className="aam-actions">
                  <button
                    type="button"
                    className="aam-btn-primary"
                    disabled={!formData.confirmed}
                    onClick={() => {
                      if (!formData.confirmed) {
                        toast.error('Please confirm that you agree to the Recruiter Agreement to continue');
                        return;
                      }
                      setStep(2);
                    }}
                  >
                    <span>Next: Provide Signature</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </>
            )}

            {/* Step 2 Form */}
            {step === 2 && (
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="aam-form-group">
                    <label className="aam-form-label">
                      First Name <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="aam-form-input"
                      placeholder="Enter first name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="aam-form-group">
                    <label className="aam-form-label">
                      Last Name <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="aam-form-input"
                      placeholder="Enter last name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="aam-form-group">
                  <label className="aam-form-label">
                    Physical Address <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <textarea
                    className="aam-form-input"
                    placeholder="Enter your street address, city, state and ZIP code"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    required
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div className="aam-form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label className="aam-form-label" style={{ margin: 0 }}>
                      Draw Your E-Signature <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    {signature && (
                      <button type="button" onClick={clearSignature} className="aam-clear-btn">
                        Clear Signature
                      </button>
                    )}
                  </div>

                  <div className="aam-signature-box">
                    <canvas
                      ref={canvasRef}
                      className="aam-signature-canvas"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>
                    Draw your signature with your mouse or touchscreen. By clicking &quot;Sign Recruiter Agreement&quot;, you acknowledge that your electronic signature is legally valid and enforceable.
                  </p>
                </div>

                <div className="aam-actions">
                  <button
                    type="button"
                    style={{
                      padding: '10px 18px',
                      background: '#F1F5F9',
                      border: '1px solid #CBD5E1',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#475569',
                      cursor: 'pointer'
                    }}
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>

                  <button
                    type="submit"
                    className="aam-btn-primary"
                    disabled={
                      submitting ||
                      !formData.firstName ||
                      !formData.lastName ||
                      !formData.address ||
                      !signature
                    }
                  >
                    {submitting ? (
                      <>
                        <div className="aam-spinner" />
                        <span>Signing Agreement...</span>
                      </>
                    ) : (
                      <span>Sign Recruiter Agreement</span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Full Document PDF Preview Overlay */}
      {showPreview && (
        <div className="aam-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="aam-preview-card" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="aam-preview-close"
              onClick={() => setShowPreview(false)}
              aria-label="Close preview"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 5L15 15M15 5L5 15" stroke="#2F4134" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <iframe
              src="Affiliate_Agreement.pdf"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Recruiter Agreement Preview"
            />
          </div>
        </div>
      )}

      {/* Success Confirmation Modal */}
      {showSuccessModal && (
        <div className="aam-overlay">
          <div className="aam-wrapper aam-success-wrapper" onClick={(e) => e.stopPropagation()}>
            <div className="aam-success-icon">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h3 className="aam-success-title">Agreement Signed Successfully!</h3>
            <p className="aam-success-desc">
              Thank you for digitally signing the Recruiter Agreement. Your agreement has been recorded and your access to all portal features is now active.
            </p>
            <button
              type="button"
              className="aam-btn-primary"
              style={{ width: '100%' }}
              onClick={handleFinish}
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}
    </>
  );
}

AffiliateAgreementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func
};
